import { useEffect, useLayoutEffect, useRef } from "react";
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type Mutation,
  type QueryClient,
} from "@tanstack/react-query";
import { startRegistration } from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import {
  deletePasskey,
  listPasskeys,
  regenerateRecoveryCodes,
  renamePasskey,
  startPasskeyRegistration,
  verifyPasskeyRegistration,
  type DeletePasskeyResponse,
  type PasskeyListResponse,
  type PasskeyRegisterVerifyResponse,
  type RecoveryCodesResponse,
} from "../api/passkeys.api";
import {
  ApiError,
  getAccessKey,
  isAccessKeyHandoff,
  isUncertainWriteFailure,
} from "@/lib/api/client";
import {
  guardedCeremony,
  signalUnknownRegistrationCredential,
  type CeremonyOwner,
} from "@/lib/webauthn";
import { useMfaRecoveryHold } from "../mfa-recovery-hold";
import { assertStepUpProofWanted, isStepUpRequired, type StepUpProof } from "../api/stepup.api";
import {
  clearPersistedRecoveryCodesAtRisk,
  persistRecoveryCodesAtRisk,
  readPersistedRecoveryCodesAtRisk,
} from "../recovery-codes-at-risk";
import {
  ackRecoveryCodesShown,
  clearPendingRecoveryCodesGeneration,
  parkRecoveryCodesGeneration,
  readPendingRecoveryCodesGeneration,
} from "../recovery-codes-generation";

export const passkeysKeys = {
  all: ["passkeys"] as const,
  list: () => [...passkeysKeys.all, "list"] as const,
};

export const passkeysMutationKeys = {
  prepare: ["passkeys", "prepare"] as const,
  enroll: ["passkeys", "enroll"] as const,
  remove: ["passkeys", "remove"] as const,
  regenerate: ["passkeys", "regenerate"] as const,
};

export const passkeysActionMutationKeys = [
  passkeysMutationKeys.enroll,
  passkeysMutationKeys.remove,
  passkeysMutationKeys.regenerate,
] as const;

function mutationKeyMatches(
  key: readonly unknown[] | undefined,
  expected: readonly unknown[],
): boolean {
  return (
    Array.isArray(key) &&
    key.length === expected.length &&
    key.every((part, index) => part === expected[index])
  );
}

export function isPasskeysActionMutationKey(key: readonly unknown[] | undefined): boolean {
  return passkeysActionMutationKeys.some((actionKey) => mutationKeyMatches(key, actionKey));
}

export function isProvenPasskeysStepUpRejection(
  mutation: Mutation<unknown, Error, unknown, unknown>,
): boolean {
  if (!isStepUpRequired(mutation.state.error)) return false;
  const vars = mutation.state.variables;
  if (vars == null || typeof vars !== "object") return false;
  return "stepUp" in vars && (vars as { stepUp?: unknown }).stepUp != null;
}

export const passkeysReportedStepUpRejectionIdsKey = [
  "passkeys",
  "reported-step-up-rejection-ids",
] as const;

function reportedStepUpRejectionIds(queryClient: QueryClient): Set<number> {
  return new Set(queryClient.getQueryData<number[]>(passkeysReportedStepUpRejectionIdsKey) ?? []);
}

export function hasLatestProvenPasskeysStepUpRejection(queryClient: QueryClient): boolean {
  const reported = reportedStepUpRejectionIds(queryClient);
  const cache = queryClient.getMutationCache();
  for (const key of passkeysActionMutationKeys) {
    const all = cache.findAll({ mutationKey: key });
    if (all.length === 0) continue;
    const latest = all.reduce((left, right) => (left.mutationId > right.mutationId ? left : right));
    if (isProvenPasskeysStepUpRejection(latest) && !reported.has(latest.mutationId)) return true;
  }
  return false;
}

export function consumeProvenPasskeysStepUpRejections(queryClient: QueryClient): void {
  const cache = queryClient.getMutationCache();
  const reported = reportedStepUpRejectionIds(queryClient);
  let changed = false;
  for (const key of passkeysActionMutationKeys) {
    for (const mutation of cache.findAll({ mutationKey: key })) {
      if (!isProvenPasskeysStepUpRejection(mutation)) continue;
      if (!reported.has(mutation.mutationId)) {
        reported.add(mutation.mutationId);
        changed = true;
      }
      cache.remove(mutation);
    }
  }
  if (changed) {
    queryClient.setQueryData(passkeysReportedStepUpRejectionIdsKey, [...reported]);
  }
}

export const recoveryCodesAtRiskKey = ["mfa", "codes-at-risk"] as const;

export const dismissedConfirmationKey = ["mfa", "dismissed-confirmation"] as const;

export const dismissedConfirmationNoticeId = "mfa-dismissed-confirmation";

export type DismissedConfirmationSection = "passkeys" | "totp";

let recoveryCodesAtRiskGenerationValue = 0;

export function recoveryCodesAtRiskGeneration(): number {
  return recoveryCodesAtRiskGenerationValue;
}

export const PREPARED_REGISTRATION_LIFETIME_MS = 4 * 60 * 1000;

export interface PreparedPasskeyRegistration {
  response: RegistrationResponseJSON;
  startedAt: number;
  rpId: string | null;
}

export const enrollAbandonedKey = ["passkeys", "enroll-abandoned"] as const;
export const enrollExpiredKey = ["passkeys", "enroll-expired"] as const;

export function publishEnrollAbandoned(queryClient: QueryClient): void {
  queryClient.setQueryDefaults(enrollAbandonedKey, {
    gcTime: Infinity,
    staleTime: Infinity,
  });
  queryClient.setQueryData(enrollAbandonedKey, true);
}

export function clearEnrollAbandoned(queryClient: QueryClient): void {
  queryClient.setQueryData(enrollAbandonedKey, false);
}

export function useEnrollAbandoned(): boolean {
  const { data } = useQuery<boolean>({
    queryKey: enrollAbandonedKey,
    queryFn: skipToken,
    initialData: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data === true;
}

export function publishEnrollExpired(queryClient: QueryClient): void {
  queryClient.setQueryDefaults(enrollExpiredKey, {
    gcTime: Infinity,
    staleTime: Infinity,
  });
  queryClient.setQueryData(enrollExpiredKey, true);
}

export function clearEnrollExpired(queryClient: QueryClient): void {
  queryClient.setQueryData(enrollExpiredKey, false);
}

export function useEnrollExpired(): boolean {
  const { data } = useQuery<boolean>({
    queryKey: enrollExpiredKey,
    queryFn: skipToken,
    initialData: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data === true;
}

export function signalAbandonedPreparedRegistration(
  registration: PreparedPasskeyRegistration | null | undefined,
): void {
  const credentialId = registration?.response?.id;
  const rpId = registration?.rpId;
  if (!credentialId || !rpId) return;
  signalUnknownRegistrationCredential({ rpId, credentialId });
}

function registrationRpId(
  options: PublicKeyCredentialCreationOptionsJSON | { [key: string]: unknown },
): string | null {
  const rp = (options as { rp?: { id?: unknown } }).rp;
  return typeof rp?.id === "string" && rp.id.length > 0 ? rp.id : null;
}

export class PreparedRegistrationExpiredError extends Error {
  constructor() {
    super("The passkey prompt finished too long ago to confirm it.");
    this.name = "PreparedRegistrationExpiredError";
  }
}

export function setRecoveryCodesAtRisk(
  queryClient: QueryClient,
  atRisk: boolean,
  options?: { clearDuringHandoff?: boolean },
): void {
  if (!atRisk && isAccessKeyHandoff() && options?.clearDuringHandoff !== true) return;
  queryClient.setQueryData(recoveryCodesAtRiskKey, atRisk);
  if (!atRisk) {
    clearPersistedRecoveryCodesAtRisk();
    return;
  }
  recoveryCodesAtRiskGenerationValue += 1;
  persistRecoveryCodesAtRisk();
  const cached = queryClient.getQueryData<PasskeyListResponse>(passkeysKeys.list());
  if (cached?.mfaEnabled !== true) {
    void queryClient.resetQueries({ queryKey: passkeysKeys.list() });
  }
}

export function useRecoveryCodesAtRisk(): boolean {
  const { data } = useQuery<boolean>({
    queryKey: recoveryCodesAtRiskKey,
    queryFn: skipToken,
    initialData: readPersistedRecoveryCodesAtRisk,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data === true;
}

export function recoveryCodesWarningActive(
  localAtRisk: boolean,
  list: PasskeyListResponse | undefined,
): boolean {
  return localAtRisk || list?.recoveryCodesUnseen === true;
}

export function sessionKeptOrEnded(sessionKey: string | null | undefined): boolean {
  const current = getAccessKey();
  return current === sessionKey || current === null;
}

export const pendingPasskeyRecoveryCodesKey = ["mfa", "pending-passkey-recovery-codes"] as const;

export function parkPasskeyRecoveryCodes(
  queryClient: QueryClient,
  codes: string[],
  generation?: number | null,
): void {
  queryClient.setQueryDefaults(pendingPasskeyRecoveryCodesKey, {
    gcTime: Infinity,
    staleTime: Infinity,
  });
  queryClient.setQueryData(pendingPasskeyRecoveryCodesKey, codes);
  if (typeof generation === "number") {
    parkRecoveryCodesGeneration(queryClient, generation);
  }
}

export function clearPendingPasskeyRecoveryCodes(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: pendingPasskeyRecoveryCodesKey });
  clearPendingRecoveryCodesGeneration(queryClient);
}

export function usePendingPasskeyRecoveryCodes(): string[] | null {
  const { data } = useQuery<string[] | null>({
    queryKey: pendingPasskeyRecoveryCodesKey,
    queryFn: skipToken,
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  if (data === null || data === undefined || data.length === 0) return null;
  return data;
}

export function readDismissedConfirmation(
  queryClient: QueryClient,
): DismissedConfirmationSection | null {
  return (
    queryClient.getQueryData<DismissedConfirmationSection | null>(dismissedConfirmationKey) ?? null
  );
}

export function publishDismissedConfirmation(
  queryClient: QueryClient,
  section: DismissedConfirmationSection,
): void {
  queryClient.setQueryDefaults(dismissedConfirmationKey, {
    gcTime: Infinity,
    staleTime: Infinity,
  });
  queryClient.setQueryData(dismissedConfirmationKey, section);
}

export function clearDismissedConfirmation(queryClient: QueryClient): void {
  queryClient.setQueryData(dismissedConfirmationKey, null);
}

export function useDismissedConfirmation(): DismissedConfirmationSection | null {
  const { data } = useQuery<DismissedConfirmationSection | null>({
    queryKey: dismissedConfirmationKey,
    queryFn: skipToken,
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? null;
}

export function usePublishDismissedConfirmation(
  section: DismissedConfirmationSection,
  dismissedRequired: boolean,
  acknowledgeDismissed: () => void,
): void {
  const queryClient = useQueryClient();
  const current = useDismissedConfirmation();
  const publishedRef = useRef(false);
  const consumedRef = useRef(false);

  useLayoutEffect(() => {
    if (!dismissedRequired) {
      publishedRef.current = false;
      consumedRef.current = false;
      return;
    }
    if (consumedRef.current) return;
    if (readDismissedConfirmation(queryClient) === section) {
      publishedRef.current = true;
      return;
    }
    publishedRef.current = true;
    publishDismissedConfirmation(queryClient, section);
  }, [dismissedRequired, queryClient, section]);

  useLayoutEffect(() => {
    if (!publishedRef.current) return;
    if (readDismissedConfirmation(queryClient) === section) return;
    consumedRef.current = true;
    publishedRef.current = false;
    if (dismissedRequired) acknowledgeDismissed();
  }, [acknowledgeDismissed, current, dismissedRequired, queryClient, section]);
}

export function usePasskeys() {
  const queryClient = useQueryClient();
  const query = useQuery<PasskeyListResponse, ApiError | Error>({
    queryKey: passkeysKeys.list(),
    queryFn: listPasskeys,
  });

  useEffect(() => {
    const list = query.data;
    if (!list) return;
    if (
      list.recoveryCodesUnseen === true &&
      queryClient.getQueryData(recoveryCodesAtRiskKey) !== true
    ) {
      setRecoveryCodesAtRisk(queryClient, true);
    }
    const parked = readPendingRecoveryCodesGeneration(queryClient);
    if (
      list.recoveryCodesUnseen === true &&
      parked !== null &&
      parked === list.recoveryCodesGeneration
    ) {
      void ackRecoveryCodesShown(queryClient, parked).then(() => {
        void queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
      });
    }
  }, [query.data, queryClient]);

  return query;
}

export function usePreparePasskeyRegistration(ceremonyOwner: CeremonyOwner) {
  return useMutation<PreparedPasskeyRegistration, ApiError | Error, void>({
    mutationKey: passkeysMutationKeys.prepare,
    mutationFn: async () => {
      const startedAt = Date.now();
      const { options } = await startPasskeyRegistration();
      const optionsJSON = options as unknown as PublicKeyCredentialCreationOptionsJSON;
      const rpId = registrationRpId(optionsJSON);
      const response = await guardedCeremony(
        () =>
          startRegistration({
            optionsJSON,
          }),
        { owner: ceremonyOwner },
      );
      return { response, startedAt, rpId };
    },
  });
}

export function useEnrollPasskey() {
  const queryClient = useQueryClient();
  const reportHold = useMfaRecoveryHold("passkey");
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useMutation<
    PasskeyRegisterVerifyResponse,
    ApiError | Error,
    {
      registration: PreparedPasskeyRegistration;
      name?: string;
      stepUp?: StepUpProof;
      replacesRecoveryCodes?: boolean;
    },
    string | null
  >({
    mutationKey: passkeysMutationKeys.enroll,
    gcTime: Infinity,
    onMutate: () => getAccessKey(),
    mutationFn: async ({ registration, name, stepUp, replacesRecoveryCodes }) => {
      assertStepUpProofWanted(stepUp, { sendsProof: false });
      if (Date.now() - registration.startedAt > PREPARED_REGISTRATION_LIFETIME_MS) {
        throw new PreparedRegistrationExpiredError();
      }
      assertStepUpProofWanted(stepUp);
      const sessionKey = getAccessKey();
      return verifyPasskeyRegistration(registration.response, name, stepUp).catch(
        async (error: unknown) => {
          if (
            replacesRecoveryCodes === true &&
            isUncertainWriteFailure(error) &&
            sessionKeptOrEnded(sessionKey)
          ) {
            setRecoveryCodesAtRisk(queryClient, true);
          }
          if (replacesRecoveryCodes === true && stepUp === undefined && isStepUpRequired(error)) {
            await queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
          }
          throw error;
        },
      );
    },
    onSuccess: (result, _variables, sessionKey) => {
      if (
        result.recoveryCodes &&
        result.recoveryCodes.length > 0 &&
        getAccessKey() === sessionKey
      ) {
        parkPasskeyRecoveryCodes(queryClient, result.recoveryCodes, result.recoveryCodesGeneration);
        if (mountedRef.current) {
          setRecoveryCodesAtRisk(queryClient, false);
          reportHold(true);
        }
      }
      queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
    },
  });
}

export function useRenamePasskey() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, ApiError, { passkeyId: string; name: string }>({
    mutationFn: ({ passkeyId, name }) => renamePasskey(passkeyId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
    },
  });
}

export function useDeletePasskey() {
  const queryClient = useQueryClient();

  return useMutation<
    DeletePasskeyResponse,
    ApiError,
    { passkeyId: string; stepUp?: StepUpProof },
    number
  >({
    mutationKey: passkeysMutationKeys.remove,
    gcTime: Infinity,
    mutationFn: ({ passkeyId, stepUp }) => {
      assertStepUpProofWanted(stepUp);
      return deletePasskey(passkeyId, stepUp);
    },
    onMutate: () => recoveryCodesAtRiskGeneration(),
    onSuccess: (result, _variables, generation) => {
      if (result.mfaEnabled === false && generation === recoveryCodesAtRiskGeneration()) {
        setRecoveryCodesAtRisk(queryClient, false, { clearDuringHandoff: true });
      }
      if (!isAccessKeyHandoff()) {
        queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
      }
    },
  });
}

export function useRegenerateRecoveryCodes() {
  const queryClient = useQueryClient();
  const reportHold = useMfaRecoveryHold("passkey");
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useMutation<
    RecoveryCodesResponse,
    ApiError,
    { stepUp?: StepUpProof } | void,
    string | null
  >({
    mutationKey: passkeysMutationKeys.regenerate,
    gcTime: Infinity,
    onMutate: () => getAccessKey(),
    mutationFn: (vars) => {
      assertStepUpProofWanted(vars?.stepUp);
      return regenerateRecoveryCodes(vars?.stepUp);
    },
    onSuccess: (result, _variables, sessionKey) => {
      if (getAccessKey() === sessionKey) {
        parkPasskeyRecoveryCodes(queryClient, result.recoveryCodes, result.recoveryCodesGeneration);
        if (mountedRef.current) {
          reportHold(true);
          setRecoveryCodesAtRisk(queryClient, false);
        }
      }
      queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
    },
    onError: (error, _variables, sessionKey) => {
      if (isUncertainWriteFailure(error) && sessionKeptOrEnded(sessionKey)) {
        setRecoveryCodesAtRisk(queryClient, true);
      }
      if (error instanceof ApiError && error.code === "recovery_codes_mfa_disabled") {
        queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
      }
    },
  });
}
